import { Tag as AntTag } from 'antd';
import type { ReactNode } from 'react';

import type { UiBaseProps } from './types';
import { colors, fontSize, fontWeight, radius, space } from 'design';

/**
 * A small status or category label.
 *
 * antd's `Tag` takes an arbitrary `color`, which is how the app ended up with
 * tags in a dozen unrelated hues. This exposes a closed `tone` set instead —
 * each one a foreground/background pair from the feedback tokens, so the
 * contrast is right by construction rather than by whoever picked the colour.
 */
export type TagProps = UiBaseProps & {
  tone?: 'neutral' | 'brand' | 'accent' | 'success' | 'warning' | 'error' | 'info';
  icon?: ReactNode;
  closable?: boolean;
  onClose?: () => void;
  onClick?: () => void;
};

const TONES: Record<NonNullable<TagProps['tone']>, { bg: string; fg: string }> = {
  neutral: { bg: colors.surface.sunken, fg: colors.text.secondary },
  brand: { bg: colors.surface.brandSubtle, fg: colors.text.primary },
  accent: { bg: colors.brand.accentSubtle, fg: colors.text.primary },
  success: { bg: colors.feedback.success.bg, fg: colors.feedback.success.fg },
  warning: { bg: colors.feedback.error.bg, fg: colors.feedback.error.fg },
  // `onSolid`, not `onAccent`: white on `error.solid` is 2.87:1. Every status
  // carries the foreground meant for its own solid fill, precisely so a filled
  // chip does not have to guess.
  error: { bg: colors.feedback.error.solid, fg: colors.feedback.error.onSolid },
  info: { bg: colors.feedback.info.bg, fg: colors.feedback.info.fg },
};

export default function Tag({
  tone = 'neutral',
  icon,
  closable,
  onClose,
  onClick,
  testId,
  className,
  style,
  children,
}: TagProps) {
  const { bg, fg } = TONES[tone];

  return (
    <AntTag
      icon={icon}
      closable={closable}
      onClose={onClose}
      onClick={onClick}
      className={className}
      data-testid={testId}
      style={{
        backgroundColor: bg,
        color: fg,
        border: 'none',
        borderRadius: radius.pill,
        paddingInline: space.md,
        paddingBlock: space.xs,
        fontSize: fontSize.xs,
        fontWeight: fontWeight.medium,
        marginInlineEnd: 0,
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    >
      {children}
    </AntTag>
  );
}
